import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { Conversation } from "~/components/conversation";
import { FieldError } from "~/components/field-error";
import { Button } from "~/components/ui/button";
import { api } from "~/lib/api";
import { useConversation } from "~/lib/conversation";

export function ResolveConversation({ parcelId }: { parcelId: string }) {
  const qc = useQueryClient();
  const conversation = useConversation(parcelId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = conversation.data?.openTicketIds ?? [];

  if (!conversation.data || conversation.data.entries.length === 0) return null;
  if (open.length === 0) {
    return (
      <span className="text-text-faint inline-flex items-center gap-1 text-[12px]">
        <Check className="size-3.5" /> Resolved
      </span>
    );
  }

  async function resolve() {
    setBusy(true);
    setError(null);
    try {
      await Promise.all(open.map((ticketId) => api.ops.resolveTicket(ticketId)));
      await qc.invalidateQueries({ queryKey: ["conversation", parcelId] });
      void qc.invalidateQueries({ queryKey: ["ops", "conversations"] });
      void qc.invalidateQueries({ queryKey: ["ops", "unread"] });
    } catch {
      setError("Not marked resolved. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <span className="inline-flex flex-col items-end gap-1">
      <Button size="sm" variant="outline" className="max-md:h-10" loading={busy} onClick={() => void resolve()}>
        Mark resolved
      </Button>
      <FieldError>{error}</FieldError>
    </span>
  );
}

export function ParcelConversation({ parcelId, fill = false }: { parcelId: string; fill?: boolean }) {
  return (
    <Conversation
      parcelId={parcelId}
      viewer="ops"
      compact
      fill={fill}
      emptyText="No messages with this customer yet. Write below to start one."
      label="Message the customer"
      placeholder="Write a reply"
    />
  );
}
